import { createBrowserRouter, Navigate } from "react-router";
import { AppLayout } from "./components/layout/AppLayout.tsx";
import { EmptyState } from "./components/ui/kit.tsx";
import {
  AcceptInvitePage,
  ConfirmEmailPage,
  ForgotPasswordPage,
  LoginPage,
  RequireAuth,
  ResetPasswordPage,
  SignupPage,
  UnsubscribePage,
  VerifyEmailPage,
  WelcomePage,
} from "./pages/auth/AuthPages.tsx";
import { DashboardPage } from "./pages/DashboardPage.tsx";
import { NotificationsPage, SearchPage, TimelinePage } from "./pages/MiscPages.tsx";
import { ExpenseDetailPage, ExpenseFormPage, ExpenseListPage } from "./pages/money/ExpensePages.tsx";
import { LoanDetailPage, LoanFormPage, LoanListPage } from "./pages/money/FinancePages.tsx";
import { IncomeDetailPage, IncomeFormPage, IncomeListPage } from "./pages/money/IncomePages.tsx";
import {
  CommunicationDetailPage,
  CommunicationFormPage,
  CommunicationListPage,
} from "./pages/operations/CommunicationPages.tsx";
import {
  ComplianceDashboardPage,
  ComplianceDetailPage,
  ComplianceFormPage,
} from "./pages/operations/CompliancePages.tsx";
import { DocumentBrowserPage, DocumentDetailPage } from "./pages/operations/DocumentPages.tsx";
import {
  MaintenanceDetailPage,
  MaintenanceFormPage,
  MaintenanceListPage,
} from "./pages/operations/MaintenancePages.tsx";
import { TaskDetailPage, TaskFormPage, TaskListPage } from "./pages/operations/TaskPages.tsx";
import {
  ContractorDetailPage,
  ContractorFormPage,
  ContractorListPage,
  OwnerDetailPage,
  OwnerFormPage,
  OwnerListPage,
  TenantDetailPage,
  TenantFormPage,
  TenantListPage,
} from "./pages/people/PeoplePages.tsx";
import {
  PropertyFormPage,
  PropertyListPage,
  PropertyOverviewPage,
} from "./pages/properties/PropertyPages.tsx";
import {
  CustomReportPage,
  ExpenseReportPage,
  IncomeReportPage,
  PerformanceReportPage,
  PnlReportPage,
  RentReportPage,
  ReportsHomePage,
  TransactionsPage,
} from "./pages/reports/ReportPages.tsx";
import { BillingPage } from "./pages/settings/BillingPage.tsx";
import { DataSettingsPage } from "./pages/settings/DataPages.tsx";
import {
  AccountSettingsPage,
  ApplicationSettingsPage,
  AuditLogPage,
  CategoriesPage,
  ComplianceTypesPage,
  MembersPage,
  NotificationSettingsPage,
  RolesPage,
  SettingsLayout,
  StorageSettingsPage,
} from "./pages/settings/SettingsPages.tsx";
import {
  StaffAuditPage,
  StaffLayout,
  StaffOperationsPage,
  StaffSearchPage,
  StaffTeamPage,
  StaffUserPage,
} from "./pages/staff/StaffPages.tsx";
import { SelfAssessmentPage } from "./pages/tax/SelfAssessmentPage.tsx";
import { MtdPage, TaxDashboardPage, TaxReportPage } from "./pages/tax/TaxPages.tsx";
import { TenancyFormPage, TenancyListPage, TenancyOverviewPage } from "./pages/tenancies/TenancyPages.tsx";

function NotFound() {
  return (
    <EmptyState icon="alert" title="Page not found">
      The page you're looking for doesn't exist or has moved.
    </EmptyState>
  );
}

/** list / new / :id / :id/edit routes for a resource. */
function crud(
  path: string,
  pages: { list: React.ReactNode; form?: React.ReactNode; detail: React.ReactNode },
) {
  return [
    { path, element: pages.list },
    ...(pages.form
      ? [
          { path: `${path}/new`, element: pages.form },
          { path: `${path}/:id/edit`, element: pages.form },
        ]
      : []),
    { path: `${path}/:id`, element: pages.detail },
  ];
}

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  { path: "/reset-password", element: <ResetPasswordPage /> },
  { path: "/accept-invite", element: <AcceptInvitePage /> },
  { path: "/confirm-email", element: <ConfirmEmailPage /> },
  {
    path: "/staff",
    element: (
      <RequireAuth>
        <StaffLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <StaffSearchPage /> },
      { path: "teams/:id", element: <StaffTeamPage /> },
      { path: "users/:id", element: <StaffUserPage /> },
      { path: "operations", element: <StaffOperationsPage /> },
      { path: "audit", element: <StaffAuditPage /> },
    ],
  },
  { path: "/signup", element: <SignupPage /> },
  { path: "/verify-email", element: <VerifyEmailPage /> },
  { path: "/unsubscribe", element: <UnsubscribePage /> },
  {
    path: "/welcome",
    element: (
      <RequireAuth>
        <WelcomePage />
      </RequireAuth>
    ),
  },
  {
    path: "/",
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      ...crud("properties", {
        list: <PropertyListPage />,
        form: <PropertyFormPage />,
        detail: <PropertyOverviewPage />,
      }),
      ...crud("tenancies", {
        list: <TenancyListPage />,
        form: <TenancyFormPage />,
        detail: <TenancyOverviewPage />,
      }),
      ...crud("tenants", {
        list: <TenantListPage />,
        form: <TenantFormPage />,
        detail: <TenantDetailPage />,
      }),
      ...crud("owners", { list: <OwnerListPage />, form: <OwnerFormPage />, detail: <OwnerDetailPage /> }),
      ...crud("income", { list: <IncomeListPage />, form: <IncomeFormPage />, detail: <IncomeDetailPage /> }),
      ...crud("expenses", {
        list: <ExpenseListPage />,
        form: <ExpenseFormPage />,
        detail: <ExpenseDetailPage />,
      }),
      ...crud("finance", { list: <LoanListPage />, form: <LoanFormPage />, detail: <LoanDetailPage /> }),
      ...crud("tasks", { list: <TaskListPage />, form: <TaskFormPage />, detail: <TaskDetailPage /> }),
      ...crud("maintenance", {
        list: <MaintenanceListPage />,
        form: <MaintenanceFormPage />,
        detail: <MaintenanceDetailPage />,
      }),
      ...crud("communications", {
        list: <CommunicationListPage />,
        form: <CommunicationFormPage />,
        detail: <CommunicationDetailPage />,
      }),
      ...crud("compliance", {
        list: <ComplianceDashboardPage />,
        form: <ComplianceFormPage />,
        detail: <ComplianceDetailPage />,
      }),
      ...crud("contractors", {
        list: <ContractorListPage />,
        form: <ContractorFormPage />,
        detail: <ContractorDetailPage />,
      }),
      { path: "documents/upload", element: <Navigate to="/documents" replace /> },
      ...crud("documents", { list: <DocumentBrowserPage />, detail: <DocumentDetailPage /> }),
      { path: "reports", element: <ReportsHomePage /> },
      { path: "reports/pnl", element: <PnlReportPage /> },
      { path: "reports/income", element: <IncomeReportPage /> },
      { path: "reports/expenses", element: <ExpenseReportPage /> },
      { path: "reports/rent", element: <RentReportPage /> },
      { path: "reports/performance", element: <PerformanceReportPage /> },
      { path: "reports/custom", element: <CustomReportPage /> },
      { path: "reports/transactions", element: <TransactionsPage /> },
      { path: "tax", element: <TaxDashboardPage /> },
      { path: "tax/report", element: <TaxReportPage /> },
      { path: "tax/mtd", element: <MtdPage /> },
      { path: "tax/self-assessment", element: <SelfAssessmentPage /> },
      { path: "search", element: <SearchPage /> },
      { path: "notifications", element: <NotificationsPage /> },
      { path: "timeline", element: <TimelinePage /> },
      {
        path: "settings",
        element: <SettingsLayout />,
        children: [
          { index: true, element: <Navigate to="/settings/account" replace /> },
          { path: "account", element: <AccountSettingsPage /> },
          { path: "notifications", element: <NotificationSettingsPage /> },
          { path: "application", element: <ApplicationSettingsPage /> },
          { path: "members", element: <MembersPage /> },
          { path: "roles", element: <RolesPage /> },
          { path: "categories", element: <CategoriesPage /> },
          { path: "compliance-types", element: <ComplianceTypesPage /> },
          { path: "storage", element: <StorageSettingsPage /> },
          { path: "audit", element: <AuditLogPage /> },
          { path: "export", element: <DataSettingsPage /> },
          { path: "billing", element: <BillingPage /> },
        ],
      },
      { path: "*", element: <NotFound /> },
    ],
  },
]);
